import { useCallback, useEffect, useState } from "react";
import { useUrlParams } from "./use-url-params";
import { useApi } from "./use-api";
import { useUrlSetter } from "./use-url-sync";

export interface WhereValue {
  value: string; // post_name (slug)
  label: string; // clean display name
  latitude: string;
  longitude: string;
  locationType: string; // 'cities' or 'regions'
  locationId: string; // post ID
}

/**
 * Hook to manage where filter synchronization with URL parameters
 * Only handles city/region selections from autocomplete dropdown
 * Current location selections are not synced to URL
 */
export const useWhereUrl = () => {
  const { getParam, setParam, removeParam } = useUrlParams();
  const { fetchLocationBySlug } = useApi();
  
  const [isLoading, setIsLoading] = useState(true);
  const [currentWhere, setCurrentWhere] = useState<WhereValue | undefined>();

  // Initialize where value from URL on mount
  useEffect(() => {
    const loadWhereFromUrl = async () => {
      try {
        setIsLoading(true);
        const urlWhere = getParam('where');
        
        if (!urlWhere) {
          setCurrentWhere(undefined);
          return;
        }

        // Fetch location data by slug
        const locationData = await fetchLocationBySlug(urlWhere);
        
        if (locationData) {
          setCurrentWhere(locationData);
        } else {
          // Invalid URL parameter, remove it
          removeParam('where');
          setCurrentWhere(undefined);
        }
      } catch (error) {
        console.error('Failed to load location from URL:', error);
        removeParam('where');
        setCurrentWhere(undefined);
      } finally {
        setIsLoading(false);
      }
    };

    loadWhereFromUrl();
  }, [getParam, removeParam, fetchLocationBySlug]);

  /**
   * Update where value and sync with URL
   * Only sync if it's a city/region selection (not current location)
   */
  const syncWhereToUrl = useUrlSetter<WhereValue>(
    'where',
    setParam,
    removeParam,
    (where: WhereValue) => where.value,
    (where: WhereValue) => where.locationType !== 'current',
  );

  const setWhere = useCallback((where: WhereValue | undefined) => {
    setCurrentWhere(where);
    syncWhereToUrl(where);
  }, [syncWhereToUrl]);

  return {
    currentWhere,
    setWhere,
    isLoading,
  };
};